import { AfterViewInit, ChangeDetectorRef, Component, computed, ElementRef, Inject, OnInit, signal, ViewChild } from "@angular/core";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { FormsModule } from "@angular/forms";
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from "@angular/material/bottom-sheet";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { ActivatedRoute, Data } from "@angular/router";
import { Network } from "@capacitor/network";
import { AuthService } from "@core/auth.service";
import { CaptchaService } from "@core/captcha.service";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { interval, map, Subscription, switchMap } from "rxjs";
import { bounceIn } from "src/app/animations/custom.animation";
import { CommonVariable } from "src/app/app.constants";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { UserManagementService } from "src/app/services/user-management.service";

@Component({
  selector: "app-forgot-password",
  templateUrl: "./forgot-password.page.html",
  styleUrls: ["./forgot-password.page.scss"],
  animations: [bounceIn],
  standalone: true,
  imports: [IonicModule, FormsModule, MatProgressSpinner, MatIcon, TranslatePipe],
})
export class ForgotPasswordPage implements OnInit, AfterViewInit {
  selectedOption = signal<'sms' | 'email'>('sms');
  isSmsSelected = computed(() => this.selectedOption() === 'sms');
  private _email = signal<string>('');
  isEmailInvalid = signal<boolean>(false);
  uid:string;
  isEmailValid = computed(() => {
    const emailValue = this.email; // Use getter to access email value
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(emailValue); // Simple email regex validation
  });
  isEmailEmpty = computed(() => this.email.trim() === '');
  phoneNumberValid = signal<boolean>(false);
  countryCode: any;
  msn: any;
  isNumberInvalid: boolean;
  phoneInValid: boolean = true;
  isOnline: boolean = true;
  translatedToastMsg: any;
  selectedLanguage: string = "en";
  @ViewChild("phoneNumber", { static: false }) phoneNumber: ElementRef<HTMLInputElement>;
  cell1TelInput = {
    initialCountry: CommonVariable.countryCode,
    separateDialCode: true,
  };
  spinnerAnimation: boolean;
  progressBar: number;
  proceed = true;
  timer$: any;
  sub: any;
  redirectTo: any;
  inputObj: Data;
  dialCode = "+44";
  UserCountryBaseOnIPSubscription: Subscription;
  private languageSubscription: Subscription;
  private constantsMsgSubscription: Subscription;
  private otpSentSubscription: Subscription;
  public loginWithMsn = false;
  public phoneNumberWithCountryCode: string;
  public disableCTA = false;
  public userId: string;
  constructor(
    private bottomSheetRef: MatBottomSheetRef<ForgotPasswordPage>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
    private analytics: AngularFireAnalytics,
    private captchaService: CaptchaService,
    private changeDetectorRef: ChangeDetectorRef,
    private authService: AuthService,
    private route: ActivatedRoute,
    public translate: TranslateService,
    private userManagementService: UserManagementService,
    private GTMSegment: GTMSegmentService
  ) {
    // This method has been used to get
    this.getPreferredLanguage();
    // Subscribe to getConstantsMsg$ observable
    this.constantsMsgSubscription = this.authService?.getConstantsMsg$?.subscribe((msg: any) => {
      this.translatedToastMsg = msg?.static_messages_pwa;
    });
    // Log Forgot Password event
    this.analytics.logEvent("Forgot_password_modal", {
      userId: this.userId,
    });

    this.otpSentSubscription = this.captchaService?.otpSent$?.subscribe(
      (data) => {
        // Check if data is present and proceed with the OTP sent logic
        if (data) {
          this.progressBar = 100; // Set progress bar to 100%

          // Fetch constant message for OTP success
          this.authService
            .getConstantsMsg("1004")
            .then((translatedMsg) => {
              const phone = this.phoneNumberWithCountryCode || this.msn || "";
              const maskedMobile =
                phone.length > 4
                  ? `${phone.slice(0, -4).replace(/\d/g, "*")}${phone.slice(-4)}`
                  : phone;
              let translatedMessage = translatedMsg?.message;
              if (translatedMessage?.line_1?.includes("{masked_mobile_number}")) {
                translatedMessage = {
                  ...translatedMessage,
                  line_1: translatedMessage.line_1.replace(
                    /\{masked_mobile_number\}/g,
                    maskedMobile
                  ),
                };
              }
              const defaultMsg = {
                message: {
                  line_1: `We sent a verification code to ${maskedMobile}.`, // Default message
                  line_2: "", // Empty second line
                },
                style: {
                  type: "Success", // Message type
                  icon: "tick circle success.png", // Success icon
                },
              };
              // Show toast message with translated content
              this.authService.showToastMessage(
                translatedMessage ? { ...translatedMsg, message: translatedMessage } : translatedMsg,
                defaultMsg
              );
            })
            .catch((error) => {
              // Log any error encountered while fetching the constants message
              console.error("Error fetching constants message:", error);
            });

          // Delay execution of next steps to simulate progress
          setTimeout(() => {
            this.spinnerAnimation = false; // Stop spinner animation
            this.proceed = false; // Prevent further actions
            this.changeDetectorRef.detectChanges(); // Trigger change detection

            // After a small delay, reset flags and dismiss the bottom sheet
            setTimeout(() => {
              this.proceed = true; // Allow further actions
              this.disableCTA = false; // Enable CTA button
              // Dismiss bottom sheet with phone number data
              this.bottomSheetRef.dismiss({ msn: this.phoneNumberWithCountryCode,uid: this.uid });
            }, 2000); // Delay before dismissing
          }, 300); // Initial delay before triggering timeout
        }
      },
      (err) => {
        // Handle errors in OTP sent observable
        this.disableCTA = false; // Enable CTA button in case of error
        console.error("Error in OTP sent observable:", err); // Log error
      }
    );
  }

  async ngOnInit() {
    await this.authService
      ?.getUid("ForgotPasswordPage")
      ?.then((uid) => {
        this.userId = uid;
      })
      .catch((error) => {
        return error;
      });
    // Get redirect URL
    this.redirectTo = this.route.snapshot.queryParams["redirectTo"] || "/mainTabs/tabs/home";
    // Subscribe to network changes
    Network.addListener("networkStatusChange", (e) => {
      try {
        this.isOnline = e?.connected ? true : false;
      } catch (error) {
        console.error("Error handling network status change:", error);
      }
    });

    // Use optional chaining with nullish coalescing to directly assign values
    this.loginWithMsn = this.data?.loginWithMsn ?? this.loginWithMsn;
    this.msn = this.data?.mobileNumber ?? this.msn;
    this.dialCode = this.data?.countryCode ?? this.dialCode;
  }

  ngAfterViewInit(): void {
    // Focus on the phone number input element and simulate a keyup event after a delay
    setTimeout(() => {
      try {
        var myElement = this.phoneNumber?.nativeElement;
        myElement?.focus(); // Focus the input element
        let event = new KeyboardEvent("keyup", { bubbles: true }); // Create the keyup event
        myElement?.dispatchEvent(event); // Dispatch the keyup event
        this.updatePhoneNumberValidity();
      } catch (error) {
        console.error("Error focusing on phone number input:", error); // Error handling for focus event
      }
    }, 1000); // Delay of 1 second before executing the logic

    // Fetch dial code based on the user's IP address
    this.getDialCodeBasedOnIPAddress();
  }

  dismissDialog() {
    // Dismiss the bottom sheet dialog
    this.bottomSheetRef.dismiss();
  }

  // This method is used to get the preferred language from the service
  getPreferredLanguage() {
    this.languageSubscription = this.authService?.selectedlanguage$?.subscribe({
      next: (language) => {
        // Set the selected language and apply it to the translation service
        this.selectedLanguage = language;
        this.translate.use(language || "en"); // Fallback to English if no language is found
      },
      error: (err) => {
        // Handle any errors that occur while fetching the language
        console.error("Error selecting language:", err);
      },
    });
  }

  ngOnDestroy() {
    this.constantsMsgSubscription?.unsubscribe();
    this.otpSentSubscription?.unsubscribe();
    this.languageSubscription?.unsubscribe();
    this.UserCountryBaseOnIPSubscription?.unsubscribe();
    this.captchaService.captchaSolved$.next(false);
    this.captchaService.resetRecaptcha();
  }
  // Method to handle phone number input
  getPhoneNumber(event): void {
    try {
      const newPhoneNumber = event?.target?.value?.slice(0, 11)?.replace(/[^\d]/g, ""); // Remove invalid characters and truncate to 11 characters
      const isPhoneNumberValid = /^0?\d+$/.test(newPhoneNumber); // Validate phone number (digits, optional leading 0)
      this.msn = newPhoneNumber;
      this.isNumberInvalid = !isPhoneNumberValid; // Set validity flag
      if (isPhoneNumberValid) this.errorCheck(); // Check errors if valid
    } catch (error) {
      console.error("Error in phone number processing:", error);
    }
  }

  // Optimized version of checkInput method
  checkInput(e): void {
    try {
      const currentInput = e?.target?.value ?? "";
      this.dialCode = currentInput.startsWith("+") ? currentInput : "+" + currentInput;
      this.errorCheck(); // Check for errors
    } catch (error) {
      console.error("Error processing input:", error);
    }
  }

  // Optimized version of getDialCodeBasedOnIPAddress method
  getDialCodeBasedOnIPAddress(): void {
    this.UserCountryBaseOnIPSubscription = this.userManagementService
      .getUserCountryBaseOnIP()
      .pipe(
        switchMap((res: Data) => {
          const countryCode = res?.country_code ?? "GB";
          const ipstackCountryCode = `+${res?.location?.calling_code || CommonVariable.defaultDialCode}`;
          this.dialCode = ipstackCountryCode;

          return this.authService.getCurrencyAndLanguageList$.pipe(
            map((data: Data) => {
              const countryData = data[countryCode] ?? data["GB"];
              return countryData;
            })
          );
        })
      )
      .subscribe({
        next: () => { },
        error: (err) => console.error("Error fetching dial code:", err),
      });
  }

  // Optimized errorCheck method
  errorCheck(): void {
    this.isNumberInvalid = !(this.dialCode?.length > 1 && this.msn);
  }

  // Optimized startSpinner method
  startSpinner(): void {
    const isInputInvalid = this.isSmsSelected() ? !this.msn || this.dialCode?.length <= 1 : this.isEmailInvalid();
    if (isInputInvalid) {
      this.isNumberInvalid = this.phoneInValid = true;
      return;
    }
    this.spinnerAnimation = true;
    this.progressBar = 0;
    this.timer$ = interval(20);
    this.sub = this.timer$?.subscribe({
      next: (sec) => {
        this.progressBar = sec;
        this.changeDetectorRef.detectChanges();
      },
      error: console.error,
    });
  }

  recoverPassword(): void {
    if (!window.navigator.onLine || !this.isPhoneNumberValid()) return;

    this.authService.redirectTo = this.redirectTo;
    this.disableCTA = true; // Disable CTA to prevent multiple clicks

    const finalValue = this.getFormattedPhoneNumber();
    this.phoneNumberWithCountryCode = finalValue;

    this.GTMSegment.collectVisitorData(
      !this.isSmsSelected() ? finalValue : "",
      this.isSmsSelected() ? finalValue : "",
      1
    );

    this.authService?.validateuserOtpfb(finalValue)?.subscribe({
      next: (d) => {
        this.uid = d.uid;
        if (d || !this.isSmsSelected()) {
          this.handleOtpSending(finalValue);
        } else {
          this.checkUserAndRecoverPassword(finalValue);
          this.resetCTA()
        }
      },
      error: (err) => {
        // Failure tracking
        this.GTMSegment.collectVisitorData(
          !this.isSmsSelected() ? finalValue : "",
          this.isSmsSelected() ? finalValue : "",
          1,
          0
        );
        this.handleError(err)
      },
    });
  }

  // Validates phone number format and sets error flags if invalid
  private isPhoneNumberValid(): boolean {
    const isInputInvalid = this.isSmsSelected() ? !this.msn || this.dialCode?.length <= 1 : this.isEmailInvalid();
    if (isInputInvalid) {
      this.isNumberInvalid = true;
      this.phoneInValid = true;
      return false;
    }
    return true;
  }

  // Formats phone number by removing leading zero and adding dial code
  private getFormattedPhoneNumber(): string {
    const userMsn = this.msn.startsWith("0") ? this.msn.substring(1) : this.msn;
    const finalValue = this.isSmsSelected() ? (this.dialCode + userMsn) : this.email;
    return finalValue;
  }

  // Sends OTP request and handles success/failure
  private handleOtpSending(finalValue: string): void {
    this.captchaService
      .sendLoginCode(finalValue)
      .then((res: Data) => {
        if (res?.status === "success") {
          // Success tracking for OTP send
          this.GTMSegment.collectVisitorData(
            !this.isSmsSelected() ? finalValue : "",
            this.isSmsSelected() ? finalValue : "",
            1,
            1
          );
          if (this.isSmsSelected()) {
            this.captchaService.otpSent$.next(true);
          }
        } else {
          this.GTMSegment.collectVisitorData(
            !this.isSmsSelected() ? finalValue : "",
            this.isSmsSelected() ? finalValue : "",
            1,
            0
          );
          this.handleOtpError();
        }
      })
      .catch((error) => this.handleError(error));

    if (!this.isSmsSelected()) {
      this.bottomSheetRef.dismiss({ msn: this.phoneNumberWithCountryCode });
      this.showAccountVerificationWarning(
        "1153",
        "If an account with this email exists, you'll receive a reset code"
      );
      this.GTMSegment.collectVisitorData(
        !this.isSmsSelected() ? finalValue : "",
        this.isSmsSelected() ? finalValue : "",
        1,
        1
      );
    }
  }


  // Handles OTP failure
  private handleOtpError(): void {
    // this.showErrorMessage("1014", "Something went wrong!");
    this.resetCTA();
  }

  // Checks if user exists and handles password recovery process
  private checkUserAndRecoverPassword(finalValue: string): void {
    this.authService?.checkuserExitIndb(finalValue)?.subscribe({
      next: (userExists) => (userExists ? this.initiatePasswordRecovery(finalValue) : this.showUserNotFound()),
      error: this.handleError,
    });
  }
  // Displays an error message if the user is not found
  private showUserNotFound(): void {
    this.showErrorMessage("1005", "If an account matches those details, we'll send recovery instructions.");
    this.resetCTA()
  }
  // Initiates password recovery if user exists
  private initiatePasswordRecovery(finalValue: string): void {
    this.progressBar = 50;
    setTimeout(() => this.recoverPassword(), 1000);

    this.authService.userPassword(finalValue)
      .then((res: Data) => {
        if (res?.status === "rejected") {
          this.showErrorMessage(res?.error_code, "We couldn't start password recovery. Check your details and try again.");
        }
      })
      .catch((err) => {
        console.error("Error in password recovery:", err);
        this.showErrorMessage(null, "We couldn't start password recovery. Try again.");
      });
  }


  // Shows error message via toast notification
  private showErrorMessage(code: string, defaultMessage: string): void {
    this.authService
      .getConstantsMsg(code)
      .then((translatedMsg) => {
        const message = {
          message: { line_1: translatedMsg || defaultMessage, line_2: "" },
          style: { type: "Error", icon: "tick circle error.svg" },
        };
        this.authService.showToastMessage(translatedMsg, message);
      })
      .catch(this.handleError);
  }

  // Resets the CTA button and spinner
  private resetCTA(): void {
    this.disableCTA = false;
    this.spinnerAnimation = false;
    this.proceed = true;
  }

  // Global error handler
  private handleError(error): void {
    this.disableCTA = false;
    console.error(error);
    this.resetCTA();
  }

  // Validates numeric input
  numericOnly(event): boolean {
    const charcode = event.which || event.keyCode;
    return charcode >= 48 && charcode <= 57;
  }

  // Sets phone number validity based on error flag
  phoneNumberHasError(hasError: boolean): void {
    this.isNumberInvalid = !hasError;
    this.phoneInValid = !!this.phoneNumber.nativeElement.value;
  }

  // Closes the bottom sheet
  close(): void {
    this.bottomSheetRef.dismiss();
  }

  // Sets mobile number input object
  telInputObject(obj): void {
    this.inputObj = obj;
    if (this.data.mobileNumber) {
      obj.setNumber(this.data.mobileNumber);
    }
  }

  // Toggles the flags dropdown
  flagsDropdownToggle(value: boolean): void {
    CommonVariable.flagsDropdownOpened = value;
  }

  // Getter and setter for email signal
  get email(): string {
    return this._email();
  }

  set email(value: string) {
    this._email.set(value);
  }

  /**
   * Handles option change between 'sms' and 'email'.
   * Resets input fields and updates the selected option.
   */
  onOptionChange(value: 'sms' | 'email'): void {
    try {
      this.msn = this.email = '';
      this.selectedOption.set(value);
      this.isNumberInvalid = this.phoneInValid = false;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Handles email input change and updates validation state.
   */
  getEmail(event: Event): void {
    try {
      this.email = (event.target as HTMLInputElement).value;
      const isValid = this.isEmailValid();
      this.isEmailInvalid.set(!isValid);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Updates the validity state of the phone number input.
   */
  updatePhoneNumberValidity(): void {
    try {
      const phoneInput = this.phoneNumber?.nativeElement;
      if (phoneInput) {
        const value = phoneInput.value;
        this.phoneNumberValid.set(!!value?.length); // Set validity based on length
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  private showAccountVerificationWarning(msgCode: string, msg: string): void {
    // Fetch constant message for OTP success
    this.authService
      .getConstantsMsg(msgCode)
      .then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: msg, // Default message
            line_2: "", // Empty second line
          },
          style: {
            type: "info", // Message type
            icon: "tick info info.png", // Success icon
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      })
      .catch((error) => {
        console.error("Error fetching constants message:", error);
      });
  }

}
